import { useEffect, useRef, useState } from "react";
import { useAuthFrontendApis } from "@propelauth/frontend-apis-react";
import { useAuthInfo } from "@propelauth/react";
import { api } from "../api.js";
import { capture } from "../lib/posthog.js";

/**
 * Inviting somebody, in the two places that do it.
 *
 * The settings panel and the onboarding step ask the same question and have to
 * answer PropelAuth's failures the same way — "already in your team" and "orgs
 * not enabled" are not things to word twice and differently.
 *
 * The team comes from the token rather than being passed in: both callers have
 * exactly one, and a parameter would be a parameter somebody can get wrong.
 */
export function useInvite() {
  const { inviteUserToOrg } = useAuthFrontendApis();
  const auth = useAuthInfo();

  /**
   * The team, from the token — and the token is usually a step behind.
   *
   * The API makes a team for an account that has none, on its first request.
   * The browser is holding a token that was issued before that happened, so
   * it carries no membership and this reads null — which is exactly what every
   * existing account looks like on the day teams ship.
   *
   * So when the token says no team, ask for a new one. Once: a refresh that
   * comes back without a team means there genuinely is not one, and asking
   * again would be a loop against somebody's auth provider.
   */
  const team = auth.loading ? null : (auth.orgHelper?.getOrgIds?.()[0] ?? null);
  const started = useRef(false);
  /** Whether the attempt below has finished, either way. */
  const [tried, setTried] = useState(false);

  /**
   * Not "a fetch is in flight" but "we have not finished trying yet", which
   * includes the render before the effect has run. Started as a fetch flag, it
   * was false on that first render and the panel announced a failure it had
   * not attempted.
   */
  const settling = auth.loading || (!team && !tried);

  useEffect(() => {
    if (auth.loading || team || started.current) return;
    started.current = true;

    void (async () => {
      /**
       * Ask the API first, then refresh the token. Order matters.
       *
       * The API makes the team, so asking it is what guarantees one exists.
       * Refreshing first and hoping some other request had already made it is
       * a race, and it loses on exactly the page that needs this most.
       */
      await api.team().catch(() => ({ orgId: null }));
      await auth.refreshAuthInfo();
      setTried(true);
    })();
  }, [auth, team]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  const invite = async (email: string): Promise<boolean> => {
    const address = email.trim();
    if (!team || busy || address === "") return false;

    setBusy(true);
    setError(null);
    setSent(null);

    let ok = false;
    const response = await inviteUserToOrg({ org_id: team, email: address, role: "Member" });
    response.handle({
      success: () => {
        capture("team_member_invited");
        setSent(address);
        ok = true;
      },
      userAlreadyInOrg: () => setError("They are already on your team."),
      // Named plainly rather than passed through. "orgs_not_enabled" is our
      // configuration problem, not something to hand to somebody who was
      // trying to invite a colleague.
      orgsNotEnabled: () => setError("Teams are not switched on for this workspace yet."),
      noInvitePermission: () => setError("You do not have permission to invite people to this team."),
      orgMaxUsersLimitExceeded: () => setError("This team is already at its limit."),
      badRequest: (problem) => setError(problem.user_facing_errors?.email ?? "Check the address and try again."),
      unexpectedOrUnhandled: () => setError("Could not send that invitation. Try again in a moment."),
    });
    setBusy(false);
    return ok;
  };

  return { team, settling, invite, busy, error, sent, clear: () => setError(null) };
}
